/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ColumnsType } from 'antd/es/table';

import { ConfigRevision, GetConfigsResponse, NodeConfig } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { RelativeTime } from '@/components/RelativeTime';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { OsIcon } from '@/primitives/Icons';
import { Link } from '@/primitives/Link';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { Tooltip } from '@/primitives/Tooltip';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type SubtenantNodesProps = {
  configsQuery: ReturnType<typeof usePaginatedNodeConfigsQuery>;
};

export function SubtenantConfigs({ configsQuery }: SubtenantNodesProps) {
  const { t } = useMimicTranslation('nodeConfigurations');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const configsColumns: ColumnsType<NodeConfig> = [
    {
      title: <span data-testid="name">{t('table.header.name')}</span>,
      dataIndex: 'name',
      sorter: true,
      filterMode: 'menu',
      render: (name: string, record) => {
        return (
          <Link
            to={`/tenants/${tenantId}/node-configs/${record.id}`}
            style={{ fontSize: 'inherit', fontFamily: 'DM Mono' }}
            dataTestId="node-config-link"
          >
            {name}
          </Link>
        );
      },
      width: '400px',
    },
    {
      title: <span data-testid="os">{t('table.header.os')}</span>,
      dataIndex: 'os',
      sorter: true,
      filters: [
        { text: 'Windows', value: 'windows' },
        { text: 'Linux', value: 'linux' },
      ],
      render: (operatingSystem: NodeConfig['os'], nodeConfiguration: NodeConfig) => (
        <Tooltip title={`${operatingSystem} ${nodeConfiguration.os}`} placement="right">
          <div data-testid="operating-system-icon">
            <OsIcon name={operatingSystem} />
          </div>
        </Tooltip>
      ),
      width: '70px',
    },
    {
      title: <span data-testid="latestConfigRevision">{t('table.header.revision')}</span>,
      dataIndex: 'latestConfigRevision',
      sorter: true,
      render: (latestConfigRevision: ConfigRevision) => (
        <Link
          to={`/tenants/${tenantId}/node-configs/${latestConfigRevision.nodeConfigId}/revisions/${latestConfigRevision.revisionNumber}`}
          dataTestId="revision-path"
        >{`#${latestConfigRevision.revisionNumber}`}</Link>
      ),
    },
    {
      title: <span data-testid="configId">{t('table.header.id')}</span>,
      dataIndex: 'id',
      sorter: true,
      render: (_, nodeConfiguration: NodeConfig) => {
        return (
          <Text copyable={{ text: nodeConfiguration.id }} mono>
            {nodeConfiguration.id}
          </Text>
        );
      },
    },
    {
      title: <span data-testid="dateUpdated">{t('table.header.lastRevised')}</span>,
      dataIndex: 'dateUpdated',
      sorter: true,
      key: 'dateUpdated',
      render: (dateUpdated) => {
        return <RelativeTime date={dateUpdated} />;
      },
    },
  ];

  return (
    <Container style={{ marginTop: '24px' }}>
      <Title level={3}>{t('title')}</Title>
      <QueryWrapper<GetConfigsResponse> queryResult={configsQuery}>
        {(data) => (
          <PaginatedTable
            data={data}
            columns={configsColumns}
            rowKey={(config: NodeConfig) => config.id}
            onParamsChange={configsQuery.updateQueryParams}
            style={{ width: '100%' }}
          />
        )}
      </QueryWrapper>
    </Container>
  );
}
